import { apiGet, apiPost } from '@/lib/api';

/**
 * unsupported — браузер без Web Push;
 * ios-install — iPhone/iPad: пуши работают только у кабинета, добавленного на экран «Домой»;
 * остальное — Notification.permission.
 */
export type PushState = 'unsupported' | 'ios-install' | NotificationPermission;

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function pushState(): PushState {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!supported) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  return Notification.permission;
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function publicKey(): Promise<string | null> {
  const { publicKey } = await apiGet<{ publicKey: string | null }>('/push/key', { silent: true });
  return publicKey;
}

async function subscribeAndSave(key: string): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await apiPost('/push/subscribe', sub.toJSON(), { silent: true });
}

/** По нажатию «Включить уведомления». 'unavailable' — на сервере не настроены ключи. */
export async function enablePush(): Promise<NotificationPermission | 'unavailable'> {
  const key = await publicKey();
  if (!key) return 'unavailable';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  await subscribeAndSave(key);
  return 'granted';
}

/** При входе в кабинет: разрешение уже есть — подписка на сервере должна быть свежей. */
export async function syncPush(): Promise<void> {
  if (pushState() !== 'granted') return;
  try {
    const key = await publicKey();
    if (!key || !(await navigator.serviceWorker.getRegistration())) return;
    await subscribeAndSave(key);
  } catch {
    /* браузер отозвал подписку или нет сети — повторим при следующем входе */
  }
}

/** Перед выходом: иначе на общем телефоне пуши продолжат приходить прежнему пользователю. */
export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  try {
    await apiPost('/push/unsubscribe', { endpoint: sub.endpoint }, { silent: true });
  } catch {
    /* сервер удалит мёртвую подписку при первой неудачной отправке */
  }
  await sub.unsubscribe().catch(() => undefined);
}
